/* =====================================================================
   MONK storefront behavior
   - Shop drop-down menu in the header
   - Slide-out cart (Shopify AJAX Cart API, no page reloads)
   - Preorder checkout lock
   - Product purchase options (one-time / subscribe every 1, 2, 3 or 4 weeks) and gallery
   - 20% off first-order email popup
   ===================================================================== */
(() => {
  const MM = (window.MonkMate = window.MonkMate || {});
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = (MM.routes && MM.routes.root) || '/';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } }
  };

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const fmt = MM.moneyFormat || '${{amount}}';
    const value = (cents || 0) / 100;
    const withDelims = (n, d, thou, dec) => {
      const parts = n.toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(dec);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  };
  MM.formatMoney = formatMoney;

  const toast = (msg) => {
    const el = $('[data-mm-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-show');
    clearTimeout(toast.t);
    toast.t = setTimeout(() => el.classList.remove('is-show'), 2600);
  };

  /* ---------- Header + Shop drop-down ---------- */
  const header = $('[data-mm-header]');
  const dropdowns = $$('[data-mm-dd]');
  const setDropdown = (dd, open) => {
    dd.classList.toggle('is-open', open);
    $('[data-mm-dd-toggle]', dd).setAttribute('aria-expanded', String(open));
  };
  const closeDropdowns = () => dropdowns.forEach((dd) => setDropdown(dd, false));
  dropdowns.forEach((dd) => {
    const toggle = $('[data-mm-dd-toggle]', dd);
    toggle.addEventListener('click', () => {
      const open = !dd.classList.contains('is-open');
      closeDropdowns();
      setDropdown(dd, open);
    });
    // Hover opens on desktop; leaving closes it again
    const canHover = window.matchMedia('(hover: hover) and (min-width: 901px)');
    dd.addEventListener('mouseenter', () => { if (canHover.matches) setDropdown(dd, true); });
    dd.addEventListener('mouseleave', () => { if (canHover.matches) setDropdown(dd, false); });
    dd.addEventListener('focusout', (e) => { if (!dd.contains(e.relatedTarget)) setDropdown(dd, false); });
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('[data-mm-dd]')) closeDropdowns(); });
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Cart drawer ---------- */
  const drawer = $('[data-mm-drawer]');
  const overlay = $('[data-mm-overlay]');
  let flavors = [];
  try { flavors = JSON.parse(($('[data-mm-flavors]') || {}).textContent || '[]'); } catch (e) { flavors = []; }
  const flavorFor = (handle) => flavors.find((f) => (handle || '').toLowerCase().includes(f.match));
  let lastFocus = null;
  let cartState = null;

  const isLocked = (cart) => {
    if (!drawer) return false;
    if (drawer.dataset.preorderMode === 'true') return true;
    if (!cart) return drawer.dataset.hasPreorder === 'true';
    return cart.items.some((i) => i.properties && i.properties._preorder === 'true');
  };

  const openCart = () => {
    if (!drawer) return;
    closeDropdowns();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.classList.add('mm-locked');
    setTimeout(() => drawer.focus({ preventScroll: true }), 50);
  };
  const closeCart = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.classList.remove('mm-locked');
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  };
  MM.openCart = openCart;
  MM.closeCart = closeCart;

  const lineDiscount = (item) => {
    // Fallback subscriptions (no selling plan app yet) carry the discount as a hidden property.
    const pct = item.properties && parseFloat(item.properties._subscription_discount);
    if (!item.selling_plan_allocation && pct > 0) {
      return Math.round(item.final_line_price * (pct / 100));
    }
    return 0;
  };

  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const renderCart = (cart) => {
    cartState = cart;
    $$('[data-mm-cart-count]').forEach((el) => {
      if (el.textContent !== String(cart.item_count)) {
        el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
      }
      el.textContent = cart.item_count;
      el.dataset.count = cart.item_count;
      const btn = el.closest('[data-mm-cart-open]');
      if (btn) btn.setAttribute('aria-label', `Open cart, ${cart.item_count} items`);
    });
    if (!drawer) return;
    const label = $('[data-mm-cart-label]', drawer);
    if (label) label.textContent = `(${cart.item_count})`;

    const itemsEl = $('[data-mm-cart-items]', drawer);
    const emptyEl = $('[data-mm-cart-empty]', drawer);
    const foot = $('[data-mm-cart-foot]', drawer);
    emptyEl.hidden = cart.item_count > 0;
    foot.hidden = cart.item_count === 0;

    let savings = 0;
    itemsEl.innerHTML = cart.items.map((item, index) => {
      const fl = flavorFor(item.handle);
      const props = item.properties || {};
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      const planLabel = plan ? String(plan.name).replace(/&amp;/g, '&').split(', ').pop().trim() : '';
      const sub = plan ? `Subscription · ${planLabel}` : (props.Subscription ? `Subscription · ${props.Subscription}` : '');
      const fallbackOff = lineDiscount(item);
      const shown = item.final_line_price - fallbackOff;
      const alloc = item.selling_plan_allocation;
      const planOriginal = alloc && alloc.compare_at_price ? alloc.compare_at_price * item.quantity : 0;
      const original = Math.max(item.original_line_price, item.final_line_price, planOriginal);
      savings += original - shown;
      const variant = item.variant_title && !/default/i.test(item.variant_title) ? item.variant_title : '';
      const meta = [variant, sub || 'One-time purchase', fl && !variant ? fl.format : ''].filter(Boolean).map(escapeHtml).join('<br>');
      return `<div class="mm-line" data-line="${index + 1}" data-key="${escapeHtml(item.key)}">
        <a class="mm-line-img${fl && fl.pack ? ' mm-line-img--pack' : ''}" href="${escapeHtml(item.url)}" style="--fl:${fl ? fl.color : '#e8f1d8'}"><img src="${escapeHtml(fl ? fl.image : item.image)}" alt="" width="120" height="120" loading="lazy"></a>
        <div>
          <h3><a href="${escapeHtml(item.url)}">${escapeHtml(fl && fl.name ? fl.name : item.product_title)}</a></h3>
          <div class="mm-line-meta">${meta}${props._preorder === 'true' ? '<br><span class="mm-chip mm-chip--orange">Preorder</span>' : ''}</div>
          <div class="mm-line-controls">
            <div class="mm-qty">
              <button type="button" data-mm-qty="-1" aria-label="Decrease quantity">−</button>
              <input type="number" min="0" value="${item.quantity}" aria-label="Quantity for ${escapeHtml(item.product_title)}" data-mm-qty-input>
              <button type="button" data-mm-qty="1" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="mm-remove" data-mm-remove>Remove</button>
          </div>
        </div>
        <div class="mm-line-price">${original > shown ? `<s>${formatMoney(original)}</s>` : ''}${formatMoney(shown)}</div>
      </div>`;
    }).join('');

    const subtotal = cart.items.reduce((sum, i) => sum + i.final_line_price - lineDiscount(i), 0);
    $('[data-mm-cart-subtotal]', drawer).textContent = formatMoney(subtotal);
    const saveRow = $('[data-mm-cart-savings]', drawer);
    saveRow.hidden = savings <= 0;
    $('[data-mm-savings-amount]', drawer).textContent = `−${formatMoney(savings)}`;

    const upsell = $('[data-mm-upsell]', drawer);
    if (upsell) {
      const inCart = cart.items.some((i) => String(i.variant_id) === upsell.dataset.variantId);
      upsell.hidden = inCart || cart.item_count === 0;
    }

    const locked = isLocked(cart);
    document.documentElement.dataset.mmCheckoutLocked = String(locked);
    $$('[data-mm-lock-show]', drawer).forEach((el) => { el.hidden = !locked; });
    $$('[data-mm-lock-hide]', drawer).forEach((el) => { el.hidden = locked; });
  };

  const cartFetch = async (url, body) => {
    const res = await fetch(`${root}${url}`.replace('//', '/'), {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong');
    return data;
  };
  const refreshCart = () => cartFetch('cart.js').then(renderCart).catch(() => {});
  MM.refreshCart = refreshCart;

  const addToCart = async (items, { open = true } = {}) => {
    await cartFetch('cart/add.js', { items });
    const cart = await cartFetch('cart.js');
    renderCart(cart);
    if (open) openCart(); else toast('Added to your cart');
    return cart;
  };
  MM.addToCart = addToCart;

  const changeLine = async (lineEl, quantity) => {
    lineEl.classList.add('is-busy');
    try {
      const cart = await cartFetch('cart/change.js', { id: lineEl.dataset.key, quantity: Math.max(0, quantity) });
      renderCart(cart);
    } catch (err) {
      toast(err.message);
      lineEl.classList.remove('is-busy');
    }
  };

  if (drawer) {
    drawer.addEventListener('click', (e) => {
      const line = e.target.closest('.mm-line');
      if (line && e.target.closest('[data-mm-qty]')) {
        const input = $('[data-mm-qty-input]', line);
        changeLine(line, parseInt(input.value, 10) + parseInt(e.target.closest('[data-mm-qty]').dataset.mmQty, 10));
      } else if (line && e.target.closest('[data-mm-remove]')) {
        changeLine(line, 0);
      } else if (e.target.closest('[data-mm-locked-checkout]')) {
        const email = $('#mm-notify-email', drawer);
        toast('Checkout opens at launch. Leave your email for a heads-up.');
        if (email) email.focus();
      }
    });
    drawer.addEventListener('change', (e) => {
      if (!e.target.matches('[data-mm-qty-input]')) return;
      changeLine(e.target.closest('.mm-line'), parseInt(e.target.value, 10) || 0);
    });
    drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = $$('a[href],button:not([disabled]),input:not([type=hidden])', drawer).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    overlay.addEventListener('click', closeCart);
    refreshCart();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-mm-cart-open]');
    if (opener && drawer) { e.preventDefault(); openCart(); return; }
    if (e.target.closest('[data-mm-cart-close]')) { closeCart(); return; }

    // Quick add buttons (cards, cart add-on)
    const quick = e.target.closest('[data-mm-add]');
    if (quick) {
      e.preventDefault();
      const item = { id: Number(quick.dataset.variantId), quantity: 1 };
      if (quick.dataset.preorder === 'true') item.properties = { _preorder: 'true' };
      const label = quick.textContent;
      quick.textContent = 'Adding…';
      addToCart([item]).catch((err) => toast(err.message)).finally(() => { quick.textContent = label; });
      return;
    }

    // Any stray checkout link while locked
    const checkoutLink = e.target.closest('a[href*="/checkout"]');
    if (checkoutLink && document.documentElement.dataset.mmCheckoutLocked === 'true') {
      e.preventDefault();
      openCart();
    }
  });

  // Block any checkout form submission while locked (cart page, other apps' buttons).
  document.addEventListener('submit', (e) => {
    const submitter = e.submitter;
    const goingToCheckout = (submitter && submitter.name === 'checkout') || /\/checkout/.test(e.target.getAttribute('action') || '');
    if (goingToCheckout && document.documentElement.dataset.mmCheckoutLocked === 'true') {
      e.preventDefault();
      openCart();
      toast('Checkout opens at launch.');
    }
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.classList.contains('is-open')) closeCart();
    else if (dropdowns.some((dd) => dd.classList.contains('is-open'))) {
      const open = dropdowns.find((dd) => dd.classList.contains('is-open'));
      closeDropdowns();
      $('[data-mm-dd-toggle]', open).focus();
    }
    else if (popup && popup.classList.contains('is-open')) closePopup();
  });

  /* ---------- Product page ---------- */
  $$('[data-mm-product]').forEach((section) => {
    const form = $('[data-mm-product-form]', section);
    if (!form) return;
    const data = JSON.parse($('[data-mm-product-json]', section).textContent);
    const qtyInput = $('[data-mm-qty-input]', form);
    const addBtn = $('[data-mm-add-btn]', form);
    const addSub = $('[data-mm-add-sub]', form);
    const priceNow = $('[data-mm-price-now]', section);
    const priceWas = $('[data-mm-price-was]', section);
    const unitEl = $('[data-mm-price-unit]', section);
    const sticky = $('[data-mm-sticky]', section);

    const selection = () => {
      const mode = (form.querySelector('input[name="mm-plan"]:checked') || {}).value || 'once';
      const freq = form.querySelector('input[name="mm-freq"]:checked');
      return { mode, freq };
    };
    const update = () => {
      const { mode, freq } = selection();
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      let unitPrice = data.price;
      if (mode === 'sub' && freq) unitPrice = parseInt(freq.dataset.price, 10);
      priceNow.textContent = formatMoney(unitPrice);
      priceWas.hidden = unitPrice >= data.price;
      priceWas.textContent = formatMoney(data.price);
      if (unitEl && data.cans) unitEl.textContent = `${formatMoney(Math.round(unitPrice / data.cans))} per carton`;
      const freqLabel = mode === 'sub' && freq ? ` · ${freq.dataset.label}` : '';
      // With an app's picker, the app shows the prices, so the button only shows the one-time total.
      addSub.textContent = `${formatMoney(unitPrice * qty)}${freqLabel}`;
      addSub.hidden = mode === 'app';
      if (sticky) $('[data-mm-sticky-price]', sticky).textContent = `${formatMoney(unitPrice)}${freqLabel}`;
    };
    form.addEventListener('change', update);
    // App picker mode: if the subscription app's widget never shows up (e.g. its embed is off in this theme), bring back our own box.
    const ownPlans = $('[data-mm-plan-fallback]', form);
    if (ownPlans) {
      const appReady = () => !!section.querySelector('[name="selling_plan"], [class*="supr" i], [id*="supr" i], [class*="super-sub" i], .mm-app-plans > *');
      let tries = 0;
      const timer = setInterval(() => {
        if (appReady()) { clearInterval(timer); return; }
        if (++tries >= 12) {
          clearInterval(timer);
          ownPlans.hidden = false;
          const appRadio = form.querySelector('input[name="mm-plan"][value="app"]');
          if (appRadio) appRadio.remove();
          const sub = form.querySelector('input[name="mm-plan"][value="sub"]');
          if (sub) sub.checked = true;
          update();
        }
      }, 250);
    }
    $$('[data-mm-step]', form).forEach((b) => b.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + parseInt(b.dataset.mmStep, 10));
      update();
    }));
    qtyInput.addEventListener('input', update);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const { mode, freq } = selection();
      const item = { id: data.variantId, quantity: Math.max(1, parseInt(qtyInput.value, 10) || 1), properties: {} };
      if (data.preorder) item.properties._preorder = 'true';
      if (mode === 'app') {
        const pick = '[name="selling_plan"]:checked, select[name="selling_plan"], input[type="hidden"][name="selling_plan"]';
        const planInput = form.querySelector(pick) || section.querySelector(pick);
        if (planInput && planInput.value) item.selling_plan = Number(planInput.value);
      } else if (mode === 'sub' && freq) {
        if (freq.dataset.sellingPlan) item.selling_plan = Number(freq.dataset.sellingPlan);
        else {
          item.properties.Subscription = freq.dataset.label;
          item.properties._subscription_discount = String(data.subscriptionDiscount);
        }
      }
      addBtn.classList.add('is-loading');
      addToCart([item]).catch((err) => toast(err.message)).finally(() => addBtn.classList.remove('is-loading'));
    });
    update();

    // Gallery
    const slides = $$('[data-mm-slide]', section);
    const thumbs = $$('[data-mm-thumb]', section);
    const dots = $$('[data-mm-dot]', section);
    const show = (i) => {
      slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      dots.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
    };
    thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    dots.forEach((t, i) => t.addEventListener('click', () => show(i)));
    const main = $('.mm-gallery-main', section);
    if (main && slides.length > 1) {
      let x0 = null;
      main.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const cur = slides.findIndex((s) => s.classList.contains('is-active'));
        if (Math.abs(dx) > 40) show((cur + (dx < 0 ? 1 : -1) + slides.length) % slides.length);
        x0 = null;
      });
    }

    // Sticky add bar on small screens once the main button scrolls away
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(addBtn);
      $('[data-mm-sticky-add]', sticky).addEventListener('click', () => form.requestSubmit());
    }
  });

  /* ---------- AJAX customer forms (popup, cart waitlist) ---------- */
  const submitCustomerForm = async (form) => {
    const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
    // Shopify may ask for a captcha; fall back to a normal submit so the shopper can complete it.
    if (/challenge/.test(res.url)) { form.submit(); return 'challenge'; }
    const html = await res.text();
    if (/errors|form--error|form-message--error/.test(html) && !/posted_successfully|customer_posted=true/.test(res.url + html)) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const err = doc.querySelector('.errors, .form-message--error');
      if (err && /email/i.test(err.textContent)) throw new Error(err.textContent.trim());
    }
    return 'ok';
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-mm-ajax-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button[type=submit]');
    const msg = form.querySelector('[data-mm-form-msg]');
    if (btn) btn.disabled = true;
    try {
      const result = await submitCustomerForm(form);
      if (result === 'challenge') return;
      store.set('mm_subscribed', '1');
      if (form.dataset.mmAjaxForm === 'popup') popupSuccess();
      else if (msg) { msg.hidden = false; msg.textContent = "You're on the launch list. We'll email you first."; form.querySelector('.mm-notify, .mm-form').hidden = true; }
    } catch (err) {
      if (msg) { msg.hidden = false; msg.textContent = err.message || 'Please check your email and try again.'; }
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  /* ---------- 20% off popup ---------- */
  const popup = $('[data-mm-pop]');
  const teaser = $('[data-mm-pop-teaser]');
  let popLastFocus = null;
  const openPopup = () => {
    if (!popup) return;
    popLastFocus = document.activeElement;
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mm-locked');
    if (teaser) teaser.classList.remove('is-show');
    store.set('mm_pop_seen', String(Date.now()));
    setTimeout(() => { const i = $('input[type=email]', popup); if (i && !popup.classList.contains('is-success')) i.focus({ preventScroll: true }); }, 80);
  };
  function closePopup() {
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mm-locked');
    if (teaser && store.get('mm_subscribed') !== '1' && store.get('mm_teaser_off') !== '1') teaser.classList.add('is-show');
    if (popLastFocus) popLastFocus.focus({ preventScroll: true });
  }
  function popupSuccess() {
    if (!popup) return;
    popup.classList.add('is-success');
    if (!popup.classList.contains('is-open')) openPopup();
    const copy = $('[data-mm-copy]', popup);
    if (copy) copy.focus();
  }
  MM.openPopup = openPopup;
  if (popup) {
    const delay = parseInt(popup.dataset.delay || '4', 10) * 1000;
    const days = parseInt(popup.dataset.days || '14', 10);
    const seen = parseInt(store.get('mm_pop_seen') || '0', 10);
    const fresh = Date.now() - seen > days * 864e5;
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && store.get('mm_pop_pending') === '1') {
      store.set('mm_pop_pending', '0');
      popupSuccess();
    } else if (popup.dataset.enabled === 'true' && fresh && store.get('mm_subscribed') !== '1') {
      setTimeout(() => { if (!document.body.classList.contains('mm-locked')) openPopup(); }, delay);
    } else if (teaser && store.get('mm_subscribed') !== '1' && store.get('mm_teaser_off') !== '1' && popup.dataset.enabled === 'true') {
      teaser.classList.add('is-show');
    }
    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-mm-pop-close]')) closePopup();
      const copy = e.target.closest('[data-mm-copy]');
      if (copy) {
        const code = copy.dataset.mmCopy;
        (navigator.clipboard ? navigator.clipboard.writeText(code) : Promise.reject()).then(() => { copy.textContent = 'Copied'; }).catch(() => { copy.textContent = code; });
      }
    });
    popup.addEventListener('submit', () => store.set('mm_pop_pending', '1'), true);
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-mm-teaser-close]')) { store.set('mm_teaser_off', '1'); teaser.classList.remove('is-show'); return; }
    if (e.target.closest('[data-mm-pop-open]')) { e.preventDefault(); openPopup(); }
  });

  /* ---------- Where a new page starts ----------
     A link to another page always lands at the very top; a link with a section anchor
     (like "The inner circle" -> #mm-signup) lands on that section. Back/forward keeps the browser's own position. */
  const navEntry = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
  if (!navEntry || navEntry.type === 'navigate') {
    const land = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const target = id && document.getElementById(id);
      if (target) target.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    };
    land();
    window.addEventListener('load', land, { once: true });
  }

  /* ---------- Smooth in-page anchors ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#mm-"]');
    if (!link || e.defaultPrevented) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    const target = document.getElementById(url.hash.slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });
})();

/* ---------- Click-to-enlarge pictures (vitamins and minerals tables) ---------- */
(() => {
  let box = null;
  let lastFocus = null;
  const close = () => {
    if (!box) return;
    box.classList.remove('is-open');
    document.body.classList.remove('mm-locked');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };
  const open = (trigger) => {
    const thumb = trigger.querySelector('img');
    if (!box) {
      box = document.createElement('div');
      box.className = 'mm-lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.innerHTML = '<button type="button" class="mm-lightbox-close" aria-label="Close enlarged picture">&#10005;</button><div class="mm-lightbox-scroll"><img alt=""></div>';
      box.addEventListener('click', (e) => { if (e.target === box || e.target.closest('.mm-lightbox-close') || e.target.closest('.mm-lightbox-scroll') === e.target) close(); });
      document.body.appendChild(box);
    }
    lastFocus = trigger;
    const img = box.querySelector('img');
    img.src = trigger.dataset.mmZoom;
    img.alt = thumb ? thumb.alt : '';
    box.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Enlarged picture');
    box.classList.add('is-open');
    document.body.classList.add('mm-locked');
    box.querySelector('.mm-lightbox-close').focus();
  };
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-mm-zoom]');
    if (trigger) { e.preventDefault(); open(trigger); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && box && box.classList.contains('is-open')) close();
  });
})();
